import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, 
  Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, 
  TablePagination, TextField, InputAdornment,
  Skeleton, Tooltip, MenuItem, FormControl, Select, Checkbox,
  alpha, useTheme
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { useFolderCache } from '../../hooks/useFolderCache';
import { 
  getRelativeTimeFromEpochDaysOnly,
  isEpochExpired,
  isSameMonthYear
} from '../../utils/dateUtils';



// Enhanced styled components for enterprise UI
const EnterpriseContainer = styled(Box)(({ theme }) => ({
  maxHeight: '70vh',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const CompactHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 3, 1, 3), // Reduced padding further
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 50, // Reduced height
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 24,
    right: 24,
    height: '1px',
    background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.3)} 50%, transparent 100%)`,
    borderRadius: '1px',
  },
}));

const TableContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
}));

const EnterpriseTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    backdropFilter: 'blur(10px)',
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      color: alpha(theme.palette.text.primary, 0.9),
      padding: theme.spacing(2.5, 2.5),
      borderBottom: 'none',
      position: 'relative',
      background: 'transparent',
      '&:not(:last-child)::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: '30%',
        height: '40%',
        width: '1px',
        background: alpha(theme.palette.divider, 0.2),
      },
      '& .MuiTypography-root': {
        fontWeight: 'inherit',
        fontSize: 'inherit',
        letterSpacing: 'inherit',
        textTransform: 'inherit',
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      transition: 'all 0.2s ease-in-out',
      '&:nth-of-type(even)': {
        backgroundColor: alpha(theme.palette.action.hover, 0.02),
      },
      '&:hover': {
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
        transform: 'translateX(4px)',
        boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
        '& .hover-actions': {
          opacity: 1,
          transform: 'translateX(0)',
        },
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.2, 2), // Reduced padding for compact rows
      borderBottom: 'none',
      fontSize: '0.875rem',
      verticalAlign: 'middle',
      position: 'relative',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  height: 28,
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: 6,
  textTransform: 'capitalize',
  minWidth: 80,
  '& .MuiChip-label': {
    paddingX: theme.spacing(1.5),
  },
  ...(status === 'private' && {
    backgroundColor: alpha(theme.palette.error.main, 0.08),
    color: theme.palette.error.dark,
    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
    '& .MuiChip-icon': {
      color: theme.palette.error.main,
    },
  }),
  ...(status === 'public' && {
    backgroundColor: alpha(theme.palette.success.main, 0.08),
    color: theme.palette.success.dark,
    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
    '& .MuiChip-icon': {
      color: theme.palette.success.main,
    },
  }),
}));

const CompactIconButton = styled(IconButton)(({ theme }) => ({
  padding: 6,
  borderRadius: 8,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'scale(1.05)',
  },
  '&.delete-btn': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.main,
    },
  },
  '&.copy-btn': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.info.main, 0.08),
      color: theme.palette.info.main,
    },
  },
}));

const HoverActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%) translateX(8px)',
  display: 'flex',
  gap: 6,
  opacity: 0,
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  borderRadius: 10,
  padding: theme.spacing(0.5, 1),
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backdropFilter: 'blur(8px)',
}));

const ClickableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    '& .hover-actions': {
      opacity: 1,
    },
  },
}));

const SnippetHistory = () => {
  // Get auth context safely with fallback values
  const auth = useAuth() || { user: null, isAuthenticated: false };
  const { user, isAuthenticated } = auth;
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  
  const navigate = useNavigate();
  const [snips, setSnips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    visibility: 'all', // 'all', 'public', 'private'
    expiration: 'all', // 'all', 'expired', 'active', 'never'
    tag: 'all'
  });
  const [sortOption, setSortOption] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Folder cache for mapping folder IDs to names
  const { folderMap, getFolderName, loadFolders } = useFolderCache();
  

  
  
  // Calculate statistics
  const stats = useMemo(() => {
    const total = snips.length;
    const publicCount = snips.filter(snip => !snip.isPrivate).length;
    const privateCount = snips.filter(snip => snip.isPrivate).length;
    const thisMonth = snips.filter(snip => {
      return isSameMonthYear(snip.createdAt);
    }).length;
    
    return { total, public: publicCount, private: privateCount, thisMonth };
  }, [snips]);
  
  // Extract unique tags for filter dropdown
  const availableTags = useMemo(() => {
    const tags = snips
      .flatMap(snip => snip.tags || [])
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
    return ['all', ...tags];
  }, [snips]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchSnips();
  }, [isAuthenticated, navigate, refreshTrigger]);
  
  const fetchSnips = async () => {
    try {
      setLoading(true);
      
      // Load both snippets and folders
      const [snippetsData] = await Promise.all([
        api.snippets.getUserSnippets(user?.userId),
        loadFolders() // Ensure folders are loaded for mapping
      ]);
      
      setSnips(snippetsData);
      setError(null);
    } catch (err) {
      setError('Failed to load your snippets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleCreateNew = () => {
    navigate('/create');
  };
  

  
  const handleView = (snipId) => {
    navigate(`/snippet/${snipId}`);
  };
  
  const handleDelete = async (snipId) => {
    try {
      await api.snippets.delete(snipId);
      setSnips(snips.filter(snip => snip.snipId !== snipId));
      enqueueSnackbar('Snippet deleted successfully', { 
        variant: 'success',
        action: (key) => (
          <Button color="inherit" size="small" onClick={() => {}}>
            UNDO
          </Button>
        )
      });
    } catch (err) {
      enqueueSnackbar('Failed to delete snippet', { variant: 'error' });
    }
  };
  
  const confirmDelete = (snipId) => {
    if (window.confirm('Are you sure you want to delete this snippet?')) {
      handleDelete(snipId);
    }
  };
  
  const [selectedSnippets, setSelectedSnippets] = useState(new Set());

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedSnippets(new Set(paginatedSnips.map(snip => snip.snipId)));
    } else {
      setSelectedSnippets(new Set());
    }
  };

  const handleSelectSnippet = (snipId) => {
    const newSelected = new Set(selectedSnippets);
    if (newSelected.has(snipId)) {
      newSelected.delete(snipId);
    } else {
      newSelected.add(snipId);
    }
    setSelectedSnippets(newSelected);
  };

  const handleMultiDelete = async () => {
    if (selectedSnippets.size === 0) return;
    
    if (window.confirm(`Delete ${selectedSnippets.size} selected snippets?`)) {
      try {
        await Promise.all([...selectedSnippets].map(id => api.snippets.delete(id)));
        setSnips(snips.filter(snip => !selectedSnippets.has(snip.snipId)));
        setSelectedSnippets(new Set());
        enqueueSnackbar(`${selectedSnippets.size} snippets deleted`, { variant: 'success' });
      } catch (err) {
        enqueueSnackbar('Failed to delete some snippets', { variant: 'error' });
      }
    }
  };

  const handleCopySnippet = async (snip) => {
    try {
      // Try different content fields that might exist
      const content = snip.content || snip.code || snip.text || snip.body || '';
      
      if (!content.trim()) {
        enqueueSnackbar('No content to copy', { variant: 'warning' });
        return;
      }
      
      await navigator.clipboard.writeText(content);
      enqueueSnackbar('Snippet copied to clipboard', { variant: 'success' });
    } catch (err) {
      console.error('Copy failed:', err);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = snip.content || snip.code || snip.text || snip.body || '';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        enqueueSnackbar('Snippet copied to clipboard', { variant: 'success' });
      } catch (fallbackErr) {
        enqueueSnackbar('Failed to copy snippet', { variant: 'error' });
      }
    }
  };

  const handleDeleteSnippet = (snipId) => {
    if (window.confirm('Are you sure you want to delete this snippet?')) {
      handleDelete(snipId);
    }
  };

  const handleCopyLink = (snipId) => {
    const url = `${window.location.origin}/snippet/${snipId}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        enqueueSnackbar('Link copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
      })
      .catch(err => {
        enqueueSnackbar('Failed to copy link', { variant: 'error' });
      });
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };
  
  const handleSortClose = () => {
    setSortAnchorEl(null);
  };
  
  const handleFilterChange = (filterType, value) => {
    setFilterOptions(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0);
  };
  
  const handleSortChange = (option) => {
    setSortOption(option);
    handleSortClose();
  };
  
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  const refreshSnippets = () => {
    setRefreshTrigger(prev => prev + 1);
  };
  
  // Filter snips based on search term and filter options
  const filteredSnips = useMemo(() => {
    return snips.filter(snip => {
      // Search term filter
      const matchesSearch = 
        !searchTerm || 
        snip.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        snip.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        snip.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getFolderName(snip.folderId)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (snip.tags && snip.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
      
      // Visibility filter
      const matchesVisibility = 
        filterOptions.visibility === 'all' || 
        (filterOptions.visibility === 'public' && !snip.isPrivate) ||
        (filterOptions.visibility === 'private' && snip.isPrivate);
      
      // Tag filter
      const matchesTag =
        filterOptions.tag === 'all' ||
        (snip.tags && snip.tags.includes(filterOptions.tag));
      
      // Expiration filter
      let matchesExpiration = true;
      
      if (filterOptions.expiration === 'expired') {
        matchesExpiration = isEpochExpired(snip.expiresAt);
      } else if (filterOptions.expiration === 'active') {
        matchesExpiration = snip.expiresAt && !isEpochExpired(snip.expiresAt);
      } else if (filterOptions.expiration === 'never') {
        matchesExpiration = !snip.expiresAt || snip.expiresAt === 0;
      }
      
      return matchesSearch && matchesVisibility && matchesTag && matchesExpiration;
    });
  }, [snips, searchTerm, filterOptions, getFolderName]);
  
  // Sort the filtered snippets
  const sortedSnips = useMemo(() => {
    return [...filteredSnips].sort((a, b) => {
      switch (sortOption) {
        case 'newest':
          return (b.createdAt || 0) - (a.createdAt || 0);
        case 'oldest':
          return (a.createdAt || 0) - (b.createdAt || 0);
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        default:
          return 0;
      }
    });
  }, [filteredSnips, sortOption]);
  
  // Paginate the sorted results
  const paginatedSnips = sortedSnips.slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );
  
  // Get snippet status
  const getSnippetStatus = (snip) => {
    if (isEpochExpired(snip.expiresAt)) {
      return { label: 'Expired', color: 'error', icon: <ErrorIcon fontSize="small" /> };
    } else if (snip.isPrivate) {
      return { label: 'Private', color: 'default', icon: <LockIcon fontSize="small" /> };
    } else {
      return { label: 'Public', color: 'primary', icon: <PublicIcon fontSize="small" /> };
    }
  };
  
  // Get language color based on tag
  const getLanguageColor = (tag) => {
    if (!tag) return '#607d8b'; // Default color
    
    const colors = {
      'javascript': '#f7df1e',
      'js': '#f7df1e',
      'python': '#3572A5',
      'py': '#3572A5',
      'java': '#b07219',
      'c#': '#178600',
      'csharp': '#178600',
      'php': '#4F5D95',
      'html': '#e34c26',
      'css': '#563d7c',
      'typescript': '#2b7489',
      'ts': '#2b7489',
      'ruby': '#701516',
      'rb': '#701516',
      'go': '#00ADD8',
      'golang': '#00ADD8',
      'swift': '#ffac45',
      'kotlin': '#F18E33',
      'rust': '#dea584',
      'scala': '#c22d40',
      'dart': '#00B4AB',
      'sql': '#e38c00',
      'shell': '#89e051',
      'bash': '#89e051',
      'powershell': '#012456',
      'yaml': '#cb171e',
      'yml': '#cb171e',
      'json': '#292929',
      'xml': '#0060ac',
      'markdown': '#083fa1',
      'md': '#083fa1'
    };
    
    // Try to match the tag with known languages
    const tagLower = tag.toLowerCase();
    
    // Direct match
    if (colors[tagLower]) {
      return colors[tagLower];
    }
    
    // Check if the tag contains any of the language names
    for (const [lang, color] of Object.entries(colors)) {
      if (tagLower.includes(lang)) {
        return color;
      }
    }
    
    // Generate a consistent color based on the tag string
    const hash = Array.from(tagLower).reduce(
      (hash, char) => char.charCodeAt(0) + ((hash << 5) - hash), 0
    );
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };
  
  return (
    <Box>
      {/* PAI History Header - Copy exact design from PAI Doc */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        py: 0.5,
        px: 1.5,
        mb: 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)', // Same as PAI Doc
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(8px)',
        gap: { xs: 1, md: 0 }
      }}>
        {/* Left side: Title + Action Controls */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={{ xs: 2, md: 3 }}
        >
          {/* PAI History Title */}
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1rem', md: '1.1rem' },
              letterSpacing: '-0.01em',
              mr: { xs: 0, md: 1 },
              minWidth: 'fit-content'
            }}
          >
            P<i style={{ fontStyle: 'italic', opacity: 0.8 }}>AI</i> History
          </Typography>

          {/* Elegant dot separator - Hide on mobile */}
          <Box 
            sx={{ 
              width: 3, 
              height: 3, 
              borderRadius: '50%', 
              backgroundColor: theme.palette.text.secondary,
              opacity: 0.4,
              display: { xs: 'none', md: 'block' }
            }} 
          />
        </Box>
      </Box>
      
      <EnterpriseContainer>
      {/* Compact Header */}
      <CompactHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            Snippets
            <Chip 
              label={filteredSnips.length} 
              size="small" 
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: '0.75rem'
              }} 
            />
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedSnippets.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleMultiDelete}
              size="small"
            >
              Delete ({selectedSnippets.size})
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              displayEmpty
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
              <MenuItem value="title-asc">Title A-Z</MenuItem>
              <MenuItem value="title-desc">Title Z-A</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-snippet')}
            size="small"
          >
            New Snippet
          </Button>
        </Box>
      </CompactHeader>

      {/* Table Container */}
      <TableContainer>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <EnterpriseTable stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    checked={paginatedSnips.length > 0 && selectedSnippets.size === paginatedSnips.length}
                    indeterminate={selectedSnippets.size > 0 && selectedSnippets.size < paginatedSnips.length}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ 
                      color: theme.palette.primary.main,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="inherit" component="span">Title</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="inherit" component="span">Folder</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="inherit" component="span">Status</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="inherit" component="span">Created</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton width="20px" /></TableCell>
                    <TableCell><Skeleton width="60%" /></TableCell>
                    <TableCell><Skeleton width="40%" /></TableCell>
                    <TableCell><Skeleton width="30%" /></TableCell>
                    <TableCell><Skeleton width="50%" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedSnips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      {searchTerm ? 'No snippets found matching your search.' : 'No snippets yet. Create your first snippet!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSnips.map((snip) => (
                  <ClickableRow 
                    key={snip.snipId} 
                    hover
                    onClick={(e) => {
                      // Don't navigate if clicking on checkbox or action buttons
                      if (e.target.closest('.MuiCheckbox-root') || e.target.closest('.hover-actions')) {
                        return;
                      }
                      navigate(`/snippet/${snip.snipId}`);
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSnippets.has(snip.snipId)}
                        onChange={() => handleSelectSnippet(snip.snipId)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {snip.title || 'Untitled'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {snip.tags?.slice(0, 3).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.65rem' }}
                            />
                          ))}
                          {snip.tags?.length > 3 && (
                            <Chip
                              label={`+${snip.tags.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {getFolderName(snip.folderId) || 'No folder'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={snip.isPrivate ? 'Private' : 'Public'}
                        status={snip.isPrivate ? 'private' : 'public'}
                        size="small"
                        icon={snip.isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getRelativeTimeFromEpochDaysOnly(snip.createdAt)}
                      </Typography>
                      <HoverActions className="hover-actions">
                        <Tooltip title="Copy Content">
                          <CompactIconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyLink(snip.snipId);
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </CompactIconButton>
                        </Tooltip>
                      </HoverActions>
                    </TableCell>
                  </ClickableRow>
                ))
              )}
            </TableBody>
          </EnterpriseTable>
        </Box>
        
        {/* Pagination */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1 }}>
          <TablePagination
            component="div"
            count={filteredSnips.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </Box>
      </TableContainer>
    </EnterpriseContainer>
    </Box>
  );
};

export default SnippetHistory;
